import { defineStore } from 'pinia'

import {
  getSubscription,
  createSubscriptionCheckout,
  changeSubscriptionPlan,
  reactivateSubscription,
  cancelSubscription,
} from '@/modules/settings/billingApi'
import type { SubscriptionInfo, SubscriptionStatus } from '@/types'

interface SubscriptionState {
  subscription: SubscriptionInfo | null
  isLoading: boolean
  hasLoaded: boolean
  loadError: boolean
  errorMessage: string | null
}

export const useSubscriptionStore = defineStore('subscription', {
  state: (): SubscriptionState => ({
    subscription: null,
    isLoading:    false,
    hasLoaded:    false,
    loadError:    false,
    errorMessage: null,
  }),

  getters: {
    /** Status canonico; 'none' cuando GET /subscription devolvió 404 */
    subscriptionStatus: (state): SubscriptionStatus =>
      state.subscription?.status ?? 'none',

    /**
     * Fuente de verdad para bloquear o permitir acceso al módulo WhatsApp.
     * Viene del backend — no calcular lógica paralela en el frontend.
     */
    canUseWhatsapp: (state): boolean =>
      state.subscription?.can_use_whatsapp ?? false,

    isTrialing:  (state): boolean => state.subscription?.status === 'trialing',
    isActive:    (state): boolean => state.subscription?.status === 'active',
    isPastDue:   (state): boolean => state.subscription?.status === 'past_due',
    isUnpaid:    (state): boolean => state.subscription?.status === 'unpaid',
    isCanceled:  (state): boolean => state.subscription?.status === 'canceled',

    isIncomplete: (state): boolean =>
      state.subscription?.status === 'incomplete' ||
      state.subscription?.status === 'incomplete_expired',

    isNone: (state): boolean =>
      state.subscription === null || state.subscription.status === 'none',

    isCancellationScheduled: (state): boolean =>
      state.subscription?.is_cancellation_scheduled ?? false,
  },

  actions: {
    // ── fetchSubscription ─────────────────────────────────────────────────────

    async fetchSubscription(force = false) {
      if (this.hasLoaded && !force) return
      // Evitar doble request concurrente (guard + AppLayout.onMounted en carga inicial)
      if (this.isLoading) return

      this.isLoading    = true
      this.loadError    = false
      this.errorMessage = null

      try {
        // null cuando 404 (sin suscripción) — getters devuelven 'none' / false
        this.subscription = await getSubscription()
        this.hasLoaded    = true
      } catch {
        this.loadError    = true
        this.hasLoaded    = true
        this.errorMessage = 'No se pudo cargar el estado de la suscripción.'
      } finally {
        this.isLoading = false
      }
    },

    /** Fuerza recarga ignorando el cache de hasLoaded */
    async refreshSubscription() {
      await this.fetchSubscription(true)
    },

    // ── startCheckout ─────────────────────────────────────────────────────────

    /**
     * Crea una sesión de Stripe Checkout y redirige al navegador.
     * successUrl y cancelUrl son opcionales: por defecto regresan a /settings/billing.
     * El onboarding los sobreescribe para regresar a /onboarding?checkout=success|cancel.
     * Puede lanzar 409 (ya activa) o 422 (precio no disponible).
     */
    async startCheckout(subscriptionPriceId: number, successUrl?: string, cancelUrl?: string) {
      const _successUrl = successUrl ?? `${window.location.origin}/settings/billing?checkout=success`
      const _cancelUrl  = cancelUrl  ?? `${window.location.origin}/settings/billing?checkout=cancel`
      const response    = await createSubscriptionCheckout(subscriptionPriceId, _successUrl, _cancelUrl)
      window.location.href = response.checkout_url
    },

    // ── changePlan ────────────────────────────────────────────────────────────

    /**
     * Cambia al precio indicado (upgrade/downgrade) sin salir a Stripe.
     * Actualiza el estado local si el backend confirma el cambio.
     *
     * Puede lanzar 404, 409, 422 — el llamador debe capturar y mostrar el mensaje.
     */
    async changePlan(subscriptionPriceId: number) {
      const updated     = await changeSubscriptionPlan(subscriptionPriceId)
      this.subscription = updated
    },

    // ── reactivate ───────────────────────────────────────────────────────

    /**
     * Reactiva una suscripción con cancelación programada.
     * Puede lanzar 404 o 409 — el llamador debe capturar y mostrar el mensaje.
     */
    async reactivate() {
      const updated     = await reactivateSubscription()
      this.subscription = updated
    },

    // ── cancelCurrentSubscription ────────────────────────────────────────────

    /**
     * Programa la cancelación de la suscripción al final del periodo actual.
     * Actualiza el estado local si el backend confirma el cambio.
     *
     * Puede lanzar 404 o 409 — el llamador debe capturar y mostrar el mensaje.
     */
    async cancelCurrentSubscription() {
      const updated     = await cancelSubscription()
      this.subscription = updated
    },

    // ── clearSubscriptionState ───────────────────────────────────────────────

    /** Llamado en logout para limpiar el estado */
    clearSubscriptionState() {
      this.subscription = null
      this.isLoading    = false
      this.hasLoaded    = false
      this.loadError    = false
      this.errorMessage = null
    },
  },
})
